import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRightLeft, Loader2, RefreshCw } from 'lucide-react';

const fmt = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const LayerTransfer = () => {
  const [accounts, setAccounts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [history, setHistory] = useState([]);
  const [feeSummary, setFeeSummary] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ from_key: '', to_key: '', amount: '', description: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, rt, hist, fees] = await Promise.all([
        api.get('/layer-transfer/accounts'),
        api.get('/layer-transfer/routes'),
        api.get('/layer-transfer/history'),
        api.get('/fees/summary').catch(() => ({ data: null })),
      ]);
      setAccounts(acc.data.accounts || []);
      setRoutes(rt.data.routes || []);
      setHistory(hist.data || []);
      setFeeSummary(fees.data);
    } catch (e) {
      toast.error('Failed to load layer transfer data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fromOptions = [...new Set(routes.map((r) => r.from))];
  const destOptions = routes.filter((r) => r.from === form.from_key).map((r) => r.to);

  const execute = async () => {
    if (!form.from_key || !form.to_key) { toast.error('Pick source and destination layers'); return; }
    if (!form.amount || form.amount <= 0) { toast.error('Enter an amount > 0'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/layer-transfer/execute', form);
      if (res.data.status === 'success') {
        toast.success(`Transfer ${res.data.reference} — tx ${res.data.transaction_number}`);
      } else {
        const err = res.data.error;
        toast.error(`Transfer ${res.data.status}: ${typeof err === 'string' ? err : JSON.stringify(err || '')}`);
      }
      setForm((p) => ({ ...p, amount: '', description: '' }));
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const sweepFees = async () => {
    setSweeping(true);
    try {
      const res = await api.post('/fees/sweep');
      const d = res.data;
      if (d.status === 'success') toast.success(`Swept $${d.total_usd} (${d.fee_count} fees) → Bridge · tx ${d.transaction_number}`);
      else if (d.status === 'noop') toast.info(d.message || 'Nothing to sweep');
      else toast.error(`Sweep ${d.status}: ${typeof d.error === 'string' ? d.error : JSON.stringify(d.error || '')}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="layer-transfer">
      <Card className="p-6" data-testid="layer-transfer-accounts-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
            <h3 className="text-xl font-semibold">Settlement Layer Accounts</h3>
          </div>
          <Button size="sm" variant="outline" onClick={loadAll} disabled={loading} data-testid="layer-transfer-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <div key={a.key} className="border rounded-lg p-3" data-testid={`layer-account-${a.key}`}>
              <p className="text-xs text-slate-500">{a.name} · {a.number}</p>
              <p className="font-mono text-lg text-slate-900 mt-1">{fmt(a.balance)}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Live internal transfers between your USDMint (Cyclos) settlement accounts. Each move calls the live Cyclos payment API and shows the exact result returned — no external settlement is implied.
        </p>
        {feeSummary && (
          <div className="mt-3 space-y-2" data-testid="fee-reconciliation-summary">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200">1% fee → Bridge reconciliation</Badge>
              <span className="text-slate-600 font-mono">${(feeSummary.total_usd_accrued || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} accrued</span>
              <span className="text-slate-400">· {feeSummary.accrued_count ?? feeSummary.count ?? 0} fees</span>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={sweepFees}
                disabled={sweeping || !(feeSummary.total_usd_accrued > 0)} data-testid="fee-sweep-btn">
                {sweeping ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ArrowRightLeft className="w-3 h-3 mr-1" />}
                Sweep to Bridge
              </Button>
            </div>
            {feeSummary.last_sweep && (
              <p className="text-[11px] text-slate-400" data-testid="fee-last-sweep">
                Last sweep: ${Number(feeSummary.last_sweep.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · tx {feeSummary.last_sweep.transaction_number || '—'} · {(feeSummary.last_sweep.created_at || '').slice(0, 19).replace('T', ' ')}
              </p>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="layer-transfer-form-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-emerald-600 rounded-full" />
          <h3 className="text-xl font-semibold">Move Funds Between Layers</h3>
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">LIVE API</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>From layer</Label>
            <Select value={form.from_key} onValueChange={(v) => setForm((p) => ({ ...p, from_key: v, to_key: '' }))}>
              <SelectTrigger data-testid="layer-from-select"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {fromOptions.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>To layer</Label>
            <Select value={form.to_key} onValueChange={(v) => setForm((p) => ({ ...p, to_key: v }))} disabled={!form.from_key}>
              <SelectTrigger data-testid="layer-to-select"><SelectValue placeholder="Select destination" /></SelectTrigger>
              <SelectContent>
                {destOptions.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Amount (USD)</Label>
            <Input data-testid="layer-amount-input" type="number" value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input data-testid="layer-description-input" value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
        </div>
        <Button className="mt-4" data-testid="layer-execute-btn" onClick={execute}
          disabled={submitting || !form.from_key || !form.to_key}>
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
          Execute Live Transfer
        </Button>
      </Card>

      <Card className="p-6" data-testid="layer-transfer-history-card">
        <h3 className="text-xl font-semibold mb-4">Recent Layer Transfers</h3>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">No layer transfers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="layer-transfer-history-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5">Date (UTC)</th>
                  <th className="text-left px-2 py-1.5">Reference</th>
                  <th className="text-left px-2 py-1.5">Route</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Tx #</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(t.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{t.reference}</td>
                    <td className="px-2 py-1.5">{t.route_name || `${t.from_key} → ${t.to_key}`}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(t.amount)}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{t.transaction_number || '-'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${t.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LayerTransfer;
